import {
  PoolSizeAdjustmentDirection,
  PoolSizeAdjustmentInput,
  PoolSizeAdjustmentReason,
  resolvePoolSizeAdjustment,
} from 'shared';

import { PoolSizeContext } from '../../adjust-pool-size/adjust-pool-size.validation';

export type StagedPoolSizeAdjustments = {
  /** The size the pool has once every staged adjustment is applied. */
  newPoolSize: number;
  /** What the save sends, in order; empty when the adjustments cancel out. */
  requests: PoolSizeAdjustmentInput[];
};

/** The adjustment of `reason` that takes the pool from one size to another; null for no change. */
const adjustmentBetween = (
  reason: PoolSizeAdjustmentReason,
  fromPoolSize: number,
  toPoolSize: number,
): null | PoolSizeAdjustmentInput => {
  if (fromPoolSize === toPoolSize) return null;

  return reason === PoolSizeAdjustmentReason.DATA_ENTRY_CORRECTION
    ? { reason, dilutedSharesCount: toPoolSize }
    : {
        reason,
        direction:
          toPoolSize > fromPoolSize
            ? PoolSizeAdjustmentDirection.INCREASE
            : PoolSizeAdjustmentDirection.DECREASE,
        sharesCount: Math.abs(toPoolSize - fromPoolSize),
      };
};

/**
 * Resolves the adjustments confirmed from Edit Pool, in the order they were confirmed: each starts
 * from the size the one before leaves, as the Adjust Pool Size modal showed it. Back-to-back
 * adjustments of one reason merge into one request, so redoing a staged increase records its net
 * change once in the Events Ledger; a reason switch stays a request of its own, so a formal
 * adjustment is never recorded as a correction or the other way round.
 */
export const resolveStagedPoolSizeAdjustments = ({
  adjustments,
  currentPoolSize,
  grantedShares,
}: { adjustments: PoolSizeAdjustmentInput[] } & PoolSizeContext): StagedPoolSizeAdjustments => {
  const requests: { input: PoolSizeAdjustmentInput; poolSizeBefore: number }[] = [];
  let poolSize = currentPoolSize;

  adjustments.forEach((input) => {
    const { newPoolSize } = resolvePoolSizeAdjustment({
      currentPoolSize: poolSize,
      grantedShares,
      input,
    });
    const last = requests[requests.length - 1];
    const isMerged = last?.input.reason === input.reason;
    if (isMerged) requests.pop();

    const poolSizeBefore = isMerged ? last.poolSizeBefore : poolSize;
    const request = adjustmentBetween(input.reason, poolSizeBefore, newPoolSize);
    if (request) requests.push({ input: request, poolSizeBefore });

    poolSize = newPoolSize;
  });

  return { newPoolSize: poolSize, requests: requests.map(({ input }) => input) };
};
